import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useDeviceType } from '../hooks/useDeviceType';
import { useGeolocation } from '../hooks/useGeolocation';
import NextPrayerWidget from '../components/prayer/NextPrayerWidget';
import { ResumeCard } from '../components/mobile/ResumeCard';
import { QuickActions } from '../components/mobile/QuickActions';
import { RecentSurahsCarousel } from '../components/mobile/RecentSurahsCarousel';
import { UserStats } from '../components/mobile/UserStats';
import { AuthPrompt } from '../components/mobile/AuthPrompt';
import { MiniQiblaCompass } from '../components/mobile/MiniQiblaCompass';
import { Button } from '../components/ui/button';
import { GlassCard } from '../components/shared';
import { AlertCircle, MapPin, Heart, Sparkles, ChevronRight, Loader2 } from 'lucide-react';
import { donationsApi } from '../services/api';
import { Link } from 'react-router-dom';

/**
 * MobileHomePage — page d'accueil spécifique pour mobile (Capacitor)
 * 
 * Structure:
 * 1. Section Dons - Soutenir l'application
 * 2. Section Bienvenue - Présentation Noor Al Bayan
 * 3. Section Prière prochaine
 * 4. Section Qibla + Localisation
 * 5. Section Contenu personnalisé (si authentifié) ou CTA (si non)
 * 
 * Si authentifié : dashboard personnalisé avec résumé, stats, récents, boussole
 * Si non authentifié : dashboard public avec CTA authentification + boussole
 * 
 * Géolocalisation :
 *   - Demandée au chargement (avec permission)
 *   - En cache 30 jours
 *   - Utilisée pour prières + qibla
 */

// Composant Section Dons (simplifié sans banner)
const DonationsSection = () => {
  return (
    <div className="space-y-3">
      <h2 className="text-sm uppercase tracking-wider text-text-muted font-medium">Soutenir</h2>
      
      {/* Support App Card */}
      <GlassCard className="border-primary/40">
        <div className="flex items-start gap-3 mb-3">
          <Sparkles className="w-6 h-6 text-primary shrink-0" />
          <div className="flex-1">
            <h3 className="font-serif text-base text-text-primary">Soutenir Noor El Bayen</h3>
            <p className="text-xs text-text-secondary mt-1">
              Votre don nous aide à financer l'hébergement et à maintenir cette plateforme gratuite pour tous.
            </p>
          </div>
        </div>
        <Link to="/dons" className="block">
          <Button className="bg-primary text-background w-full text-sm">
            <Heart className="w-4 h-4 mr-1" />Faire un don
          </Button>
        </Link>
      </GlassCard>
    </div>
  );
};

const MobileHomePage = () => {
  const { user, loading: authLoading } = useAuth();
  const deviceType = useDeviceType();
  const { coords, loading: geoLoading, error: geoError, permissionState, requestPosition } = useGeolocation({
    immediate: false, // On gère manuellement selon l'état de permission
    silent: true,
  });

  // Si permission déjà accordée → fetch automatique silencieux au montage
  useEffect(() => {
    if (permissionState === 'granted' && !coords && !geoLoading) {
      requestPosition();
    }
  }, [permissionState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Assurer que c'est vraiment du mobile
  if (!deviceType?.isNativeApp) {
    return null; // Laisser la page d'accueil web prendre le relais
  }

  return (
    <div 
      className="min-h-screen bg-background pb-32"
      data-testid="mobile-home-page"
    >
      {/* Header minimal */}
      <div className="px-4 pt-6 pb-4 border-b border-border/40">
        <h1 className="text-2xl font-serif text-text-primary">نور البيان</h1>
        <p className="text-xs text-text-muted mt-1">Lumière du Coran</p>
      </div>

      {/* Contenu principal */}
      <div className="px-4 py-6 space-y-6 max-w-full">
        
        {/* 1. Section Authentification (EN PREMIER) */}
        {authLoading ? (
          <div className="h-24 bg-surface-elevated rounded-lg animate-pulse" />
        ) : user ? (
          <>
            {/* UTILISATEUR AUTHENTIFIÉ */}
            <h2 className="text-sm uppercase tracking-wider text-text-muted font-medium">Mon compte</h2>
            
            {/* 1. Reprendre la lecture */}
            <ResumeCard user={user} />

            {/* 2. Statistiques personnelles */}
            <UserStats user={user} />

            {/* 3. Actions rapides */}
            <div>
              <h2 className="text-sm uppercase tracking-wider text-text-muted font-medium mb-3">
                Actions rapides
              </h2>
              <QuickActions />
            </div>

            {/* 4. Récemment consultés */}
            <RecentSurahsCarousel />
          </>
        ) : (
          <>
            {/* UTILISATEUR NON AUTHENTIFIÉ */}
            
            {/* CTA Authentification */}
            <AuthPrompt />

            {/* Actions rapides (sans historique) */}
            <div>
              <h2 className="text-sm uppercase tracking-wider text-text-muted font-medium mb-3">
                Explorer
              </h2>
              <QuickActions />
            </div>
          </>
        )}

        {/* 2. Section Dons */}
        <DonationsSection />

        {/* 3. Section : Prière prochaine */}
        <div>
          <h2 className="text-sm uppercase tracking-wider text-text-muted font-medium mb-3">Prière</h2>
          <NextPrayerWidget />
        </div>

        {/* 4. Section : Qibla */}
        <div>
          <h2 className="text-sm uppercase tracking-wider text-text-muted font-medium mb-3">Qibla</h2>
          {coords ? (
            <MiniQiblaCompass 
              coords={coords}
              loading={geoLoading}
              error={geoError}
            />
          ) : geoLoading ? (
            <div className="h-32 bg-surface-elevated rounded-lg animate-pulse" />
          ) : permissionState !== 'denied' && permissionState !== 'granted' ? (
            // Permission pas encore demandée — afficher le bouton d'activation
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 flex items-start gap-3">
              <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary font-medium">Activer la géolocalisation</p>
                <p className="text-xs text-text-muted mt-1">
                  Pour voir la direction de la Qibla et vos horaires de prière locaux
                </p>
                <Button
                  onClick={() => requestPosition(true)}
                  size="sm"
                  className="mt-2 bg-primary/20 hover:bg-primary/30 text-primary"
                  disabled={geoLoading}
                >
                  {geoLoading ? 'Localisation...' : 'Activer →'}
                </Button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Pied de page motivant */}
        {user && (
          <div className="text-center text-xs text-text-muted pt-4 border-t border-border/40">
            Continuez votre apprentissage du Coran
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileHomePage;
